import React, { useEffect, useRef, useState } from "react";
import { Link2, Check, AlertTriangle, X, Loader2, MapPin, Bed, Search, Pencil, Link as LinkIcon, ExternalLink, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

// Modal que importa un trip plan de KimKim y crea un Trip SOFI directamente
// (sin pasar por Cotización). Mantiene el mismo flujo preview→confirm que el
// import viejo del Cotizador, pero llama al endpoint `/kimkim-import/confirm-trip`
// y redirige a la ficha del Trip cuando termina.

const CONFIDENCE = {
  high: { txt: "Match KimKim", cls: "bg-emerald-600 text-white" },
  medium: { txt: "Match medio", cls: "bg-amber-200 text-amber-900" },
  low: { txt: "Match dudoso", cls: "bg-orange-200 text-orange-900" },
  manual: { txt: "Manual", cls: "bg-primary text-primary-foreground" },
};

const fmtEUR = (n) => (Number(n) || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

function InlineMatchPicker({ initialQuery, dayCity, type, onPick, onClose }) {
  const isHotel = type === "alojamiento";
  const [q, setQ] = useState(initialQuery || "");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const h = setTimeout(async () => {
      setBusy(true);
      const params = { q: (q || "").trim(), limit: 8 };
      if (isHotel) params.type = "alojamiento";
      if (dayCity) params.city = dayCity;
      try {
        const { data } = await api.get("/products", { params });
        if (!cancelled) { setResults(data.items || []); setBusy(false); }
      } catch (_e) { if (!cancelled) { setResults([]); setBusy(false); } }
    }, 220);
    return () => { cancelled = true; clearTimeout(h); };
  }, [q, dayCity, isHotel]);

  return (
    <div className="border-t bg-muted/40 px-3 py-2" data-testid="kimkim-match-picker">
      <div className="mb-2 flex items-center gap-2">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={dayCity ? `Buscar en ${dayCity}…` : "Buscar en el catálogo…"} className="h-7 flex-1 text-sm" data-testid="kimkim-picker-input" />
        <button onClick={onClose} className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground" data-testid="kimkim-picker-close">Cerrar</button>
      </div>
      {busy && <div className="py-2 text-xs text-muted-foreground">Buscando…</div>}
      {!busy && results.length === 0 && <div className="py-2 text-xs italic text-muted-foreground">Sin resultados. Prueba con menos palabras o quita el filtro de ciudad.</div>}
      {!busy && results.length > 0 && (
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {results.map((r) => {
            const total = Number(r.cost_tax_incl) || Number(r.cost_tax_excl) || 0;
            return (
              <button key={r.id} onClick={() => onPick(r)} data-testid={`kimkim-pick-${r.id}`} className="flex w-full items-center gap-2 rounded border bg-card px-2.5 py-1.5 text-left text-sm hover:border-primary hover:bg-accent">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{r.name}</div>
                  <div className="truncate text-[11px] text-muted-foreground">{[r.provider_name, r.city, r.country].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="shrink-0 text-right text-xs tabular-nums">
                  <div className="font-semibold">{fmtEUR(total)}{isHotel ? "/n" : ""}</div>
                  {!isHotel && r.pax ? <div className="text-[10px] text-muted-foreground">{r.pax} pax</div> : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function TripKimKimImport({ open, onClose, onCreated }) {
  const [step, setStep] = useState("url");
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  useEffect(() => { if (open) { setStep("url"); setUrl(""); setPreview(null); setError(null); setPickerOpen(null); } }, [open]);

  const start = async () => {
    if (!url.includes("kimkim.com")) { toast.error("La URL debe ser de kimkim.com"); return; }
    if (!url.includes("/tp/")) { toast.error("La URL debe ser un trip plan (/tp/CODE)"); return; }
    setError(null); setStep("running");
    try {
      const { data } = await api.post("/kimkim-import/preview", { url });
      pollRef.current = setInterval(() => poll(data.job_id), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error iniciando importación"); setStep("url");
    }
  };

  const poll = async (jid) => {
    try {
      const { data } = await api.get(`/kimkim-import/preview/${jid}`);
      if (data.status === "done") {
        clearInterval(pollRef.current); pollRef.current = null;
        setPreview({
          ...data.preview,
          days: (data.preview.days || []).map((d) => ({ ...d, items: (d.items || []).map((it) => ({ ...it, excluded: false })) })),
          hotels: (data.preview.hotels || []).map((h) => ({ ...h, excluded: false })),
        });
        setStep("preview");
      } else if (data.status === "error") {
        clearInterval(pollRef.current); pollRef.current = null;
        setError(data.error || "Error desconocido"); setStep("url");
      }
    } catch (e) {
      if (e?.response?.status === 404) { clearInterval(pollRef.current); pollRef.current = null; setError("Job no encontrado"); setStep("url"); }
    }
  };

  const toggleItem = (di, ii) => setPreview((p) => {
    const days = [...p.days]; const items = [...days[di].items];
    items[ii] = { ...items[ii], excluded: !items[ii].excluded }; days[di] = { ...days[di], items }; return { ...p, days };
  });
  const toggleHotel = (hi) => setPreview((p) => {
    const hotels = [...p.hotels]; hotels[hi] = { ...hotels[hi], excluded: !hotels[hi].excluded }; return { ...p, hotels };
  });

  const pickItemMatch = (di, ii, r) => {
    setPreview((p) => {
      const days = [...p.days]; const items = [...days[di].items];
      items[ii] = { ...items[ii], type: "actividad", match: {
        product_id: r.id, name: r.name, pax: r.pax || 1, city: r.city, country: r.country, provider_name: r.provider_name,
        cost_tax_excl: r.cost_tax_excl || 0, cost_tax_incl: r.cost_tax_incl || r.cost_tax_excl || 0, currency: r.currency || "EUR", confidence: "manual",
      } };
      days[di] = { ...days[di], items }; return { ...p, days };
    });
    setPickerOpen(null); toast.success("Match vinculado");
  };
  const unlinkItem = (di, ii) => setPreview((p) => {
    const days = [...p.days]; const items = [...days[di].items]; items[ii] = { ...items[ii], match: null }; days[di] = { ...days[di], items }; return { ...p, days };
  });
  const pickHotelMatch = (hi, r) => {
    setPreview((p) => {
      const hotels = [...p.hotels];
      hotels[hi] = { ...hotels[hi], match: {
        product_id: r.id, name: r.name, city: r.city, country: r.country,
        cost_tax_excl: r.cost_tax_excl || 0, cost_tax_incl: r.cost_tax_incl || r.cost_tax_excl || 0, currency: r.currency || "EUR", confidence: "manual",
      } };
      return { ...p, hotels };
    });
    setPickerOpen(null); toast.success("Hotel vinculado");
  };
  const unlinkHotel = (hi) => setPreview((p) => { const hotels = [...p.hotels]; hotels[hi] = { ...hotels[hi], match: null }; return { ...p, hotels }; });

  // Toggle Private / Small Group manual (por si KimKim no lo trajo o el operador quiere cambiarlo).
  const setGroupType = (di, ii, gt) => setPreview((p) => {
    const days = [...p.days]; const items = [...days[di].items];
    items[ii] = { ...items[ii], group_type: gt }; days[di] = { ...days[di], items }; return { ...p, days };
  });

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/kimkim-import/confirm-trip", preview);
      toast.success(`Trip creado desde KimKim · ${data.reservas} reservas · descuento 8% aplicado`);
      onClose();
      // Callback al padre: abre el TripDetail directamente en el mismo panel
      // (SOFI usa `setSelected(trip_id)` en lugar de rutas para el detail).
      onCreated?.(data.trip_id);
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(Array.isArray(det) ? det.map((d) => `${(d.loc || []).join(".")}: ${d.msg}`).join(" · ") : (det || e?.message || "Error creando trip"), { duration: 10000 });
    } finally { setConfirming(false); }
  };

  const stats = preview ? (() => {
    let matched = 0, unmatched = 0;
    (preview.days || []).forEach((d) => (d.items || []).forEach((it) => { if (it.excluded) return; if (it.match) matched++; else unmatched++; }));
    return { matched, unmatched };
  })() : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto p-0" data-testid="trip-kimkim-import-modal">
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b bg-background px-5 py-4">
          <ExternalLink className="h-5 w-5 text-primary" />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Importar desde KimKim → Trip directo</div>
            <div className="font-heading text-lg font-bold">
              {step === "url" && "Pega el link del trip plan (/tp/CODE)"}
              {step === "running" && "Leyendo y matcheando con tu catálogo…"}
              {step === "preview" && "Revisa antes de crear el Trip"}
            </div>
          </div>
        </div>

        <div className="p-5">
          {step === "url" && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Pega el link de un itinerario KimKim (ej.: <code className="rounded bg-muted px-1">https://www.kimkim.com/tp/ElgkBRAl</code>). Se crea el Trip directamente con sus reservas, aplicando el <strong>descuento 8%</strong> a las actividades (los hoteles conservan el precio KimKim). Cada item se empareja automáticamente contra el catálogo local de <strong>7.556 addons KimKim</strong>.
              </p>
              <div className="relative">
                <Link2 className="absolute left-3 top-3 h-3.5 w-3.5 text-muted-foreground" />
                <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.kimkim.com/tp/ElgkBRAl" className="pl-9" data-testid="trip-kimkim-url-input" onKeyDown={(e) => e.key === "Enter" && url && start()} />
              </div>
              {error && <div className="rounded border border-destructive bg-destructive/5 p-3 text-sm text-destructive" data-testid="trip-kimkim-error">{error}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={onClose} data-testid="trip-kimkim-cancel">Cancelar</Button>
                <Button onClick={start} disabled={!url} data-testid="trip-kimkim-start">Importar</Button>
              </div>
            </div>
          )}

          {step === "running" && (
            <div className="py-16 text-center">
              <Loader2 className="mx-auto mb-4 h-9 w-9 animate-spin text-primary" />
              <div className="font-heading text-lg font-bold">Procesando…</div>
              <div className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                Descargando el schedule desde KimKim, parseando día a día y matcheando contra el catálogo local. Suele tardar entre <span className="font-semibold">10 y 30 segundos</span>.
              </div>
            </div>
          )}

          {step === "preview" && preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-0 overflow-hidden rounded-lg border text-sm sm:grid-cols-4">
                <div className="border-r p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Cliente</div><div className="truncate font-semibold" data-testid="trip-kimkim-client">{preview.main_traveler || preview.trip_name || "—"}</div></div>
                <div className="border-r p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Fechas</div><div className="text-xs font-semibold tabular-nums">{preview.start_date || "—"} → {preview.end_date || "—"}</div><div className="text-xs text-muted-foreground">{(preview.days || []).length} días · {preview.num_travelers} pax</div></div>
                <div className="border-r p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Actividades</div><div className="font-semibold"><span className="text-emerald-600" data-testid="trip-kimkim-matched">{stats.matched}</span>{stats.unmatched > 0 && <span className="text-amber-600"> · {stats.unmatched} sin match</span>}</div></div>
                <div className="p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Precio cliente KK</div><div className="font-semibold tabular-nums">{preview.total_price ? `${preview.total_price.toLocaleString()} ${preview.total_price_currency}` : "—"}</div></div>
              </div>

              <div className="rounded border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-xs text-emerald-900" data-testid="trip-kimkim-discount-notice">
                <strong>Descuento partner KimKim (8%)</strong> se aplicará automáticamente al Cotizado Real de las reservas EXCEPTO alojamientos.
              </div>

              <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
                {(preview.days || []).map((day, di) => (
                  <div key={di} className="overflow-hidden rounded-lg border">
                    <div className="flex items-center gap-2 bg-muted px-3 py-2 text-sm">
                      <span className="text-[10px] font-bold uppercase tracking-wider">Día {day.day}</span>
                      <span className="tabular-nums text-muted-foreground">{day.date}</span>
                      {day.city && <span className="inline-flex items-center gap-1 text-muted-foreground"><MapPin className="h-3 w-3" />{day.city}</span>}
                    </div>
                    {(day.items || []).length === 0 ? <div className="px-3 py-2 text-xs italic text-muted-foreground">Sin actividades.</div> : (day.items || []).map((it, ii) => {
                      const m = it.match; const conf = m ? CONFIDENCE[m.confidence] : null; const key = `day:${di}:${ii}`; const isOpen = pickerOpen === key;
                      const specPrice = it.specialist_price ? `${it.specialist_price} ${it.specialist_currency}` : "";
                      const gt = it.group_type; // "private" | "small_group" | null
                      const showGroup = it.type === "actividad";
                      return (
                        <React.Fragment key={ii}>
                          <div className={`flex items-center gap-2 border-t px-3 py-2 ${it.excluded ? "opacity-40" : ""}`} data-testid={`trip-kimkim-item-${di}-${ii}`}>
                            <input type="checkbox" checked={!it.excluded} onChange={() => toggleItem(di, ii)} className="cursor-pointer" data-testid={`trip-kimkim-item-toggle-${di}-${ii}`} />
                            <span className="w-16 shrink-0 text-center text-[9px] uppercase tracking-wider text-muted-foreground">{it.type === "alojamiento" ? "aloj." : (it.type || "activ.")}</span>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm" title={it.kimkim_name}>{it.kimkim_name}</div>
                              {m ? (
                                <div className="flex items-center gap-2 truncate text-xs text-muted-foreground"><Check className="h-3 w-3 shrink-0 text-emerald-600" /><span className="truncate">{m.name}</span><span className={`rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${conf.cls}`}>{conf.txt}</span></div>
                              ) : (
                                <div className="flex items-center gap-2 truncate text-xs text-amber-600"><AlertTriangle className="h-3 w-3 shrink-0" /><span>Sin match · usará precio KK ({specPrice || "—"})</span></div>
                              )}
                            </div>
                            {/* Toggle Private / Small Group SOLO para actividades. En transportes/alojamiento no aparece. */}
                            {showGroup && (
                              <div className="flex shrink-0 items-center rounded-md border bg-background text-[10px] font-semibold uppercase tracking-wider" data-testid={`trip-kimkim-grouptype-${di}-${ii}`}>
                                <button onClick={() => setGroupType(di, ii, gt === "private" ? null : "private")} className={`flex items-center gap-1 rounded-l-md px-2 py-1 ${gt === "private" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} title="Marcar como Private" data-testid={`trip-kimkim-private-${di}-${ii}`}>
                                  <Users className="h-3 w-3" />Priv
                                </button>
                                <button onClick={() => setGroupType(di, ii, gt === "small_group" ? null : "small_group")} className={`flex items-center gap-1 rounded-r-md px-2 py-1 ${gt === "small_group" ? "bg-amber-500 text-white" : "text-muted-foreground hover:bg-muted"}`} title="Marcar como Small Group" data-testid={`trip-kimkim-smallgroup-${di}-${ii}`}>
                                  <Users className="h-3 w-3" />Group
                                </button>
                              </div>
                            )}
                            <div className="shrink-0 whitespace-nowrap text-right text-sm font-semibold tabular-nums">{m ? fmtEUR(m.cost_tax_incl || 0) : (specPrice || "—")}</div>
                            <div className="flex shrink-0 items-center gap-1">
                              <button onClick={() => setPickerOpen(isOpen ? null : key)} className="rounded p-1.5 text-muted-foreground hover:bg-muted" title={m ? "Cambiar match" : "Vincular al catálogo"} data-testid={`trip-kimkim-item-pick-${di}-${ii}`}>{m ? <Pencil className="h-3.5 w-3.5" /> : <LinkIcon className="h-3.5 w-3.5" />}</button>
                              {m && <button onClick={() => unlinkItem(di, ii)} className="rounded p-1.5 text-muted-foreground hover:text-destructive" title="Quitar match" data-testid={`trip-kimkim-item-unlink-${di}-${ii}`}><X className="h-3.5 w-3.5" /></button>}
                            </div>
                          </div>
                          {isOpen && <InlineMatchPicker initialQuery={it.kimkim_name} dayCity={day.city} type={it.type} onPick={(r) => pickItemMatch(di, ii, r)} onClose={() => setPickerOpen(null)} />}
                        </React.Fragment>
                      );
                    })}
                  </div>
                ))}

                {(preview.hotels || []).length > 0 && (
                  <div className="overflow-hidden rounded-lg border">
                    <div className="flex items-center gap-2 bg-primary px-3 py-2 text-[11px] uppercase tracking-wider text-primary-foreground"><Bed className="h-3.5 w-3.5" /> Alojamientos <span className="ml-auto text-[10px] font-normal opacity-80">Sin descuento 8%</span></div>
                    {(preview.hotels || []).map((h, hi) => {
                      const m = h.match; const conf = m ? CONFIDENCE[m.confidence] : null; const key = `hotel:${hi}`; const isOpen = pickerOpen === key;
                      return (
                        <React.Fragment key={hi}>
                          <div className={`flex flex-wrap items-center gap-2 border-t px-3 py-2 ${h.excluded ? "opacity-40" : ""}`} data-testid={`trip-kimkim-hotel-${hi}`}>
                            <input type="checkbox" checked={!h.excluded} onChange={() => toggleHotel(hi)} className="cursor-pointer" data-testid={`trip-kimkim-hotel-toggle-${hi}`} />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-semibold" title={h.kimkim_name}>{h.kimkim_name}</div>
                              {m ? (
                                <div className="flex items-center gap-2 truncate text-xs text-muted-foreground"><Check className="h-3 w-3 shrink-0 text-emerald-600" /><span className="truncate">{m.name}</span><span className={`rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${conf.cls}`}>{conf.txt}</span></div>
                              ) : (
                                <div className="flex items-center gap-2 truncate text-xs text-amber-600"><AlertTriangle className="h-3 w-3 shrink-0" /><span>Sin match · vincúlalo o complétalo después</span></div>
                              )}
                            </div>
                            <div className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{h.check_in || "—"} → {h.check_out || "—"}</div>
                            <div className="whitespace-nowrap text-right text-sm font-semibold tabular-nums">{m ? `${fmtEUR(m.cost_tax_incl || 0)}/n` : "—"}</div>
                            <div className="flex shrink-0 items-center gap-1">
                              <button onClick={() => setPickerOpen(isOpen ? null : key)} className="rounded p-1.5 text-muted-foreground hover:bg-muted" title={m ? "Cambiar hotel" : "Vincular al catálogo"} data-testid={`trip-kimkim-hotel-pick-${hi}`}>{m ? <Pencil className="h-3.5 w-3.5" /> : <LinkIcon className="h-3.5 w-3.5" />}</button>
                              {m && <button onClick={() => unlinkHotel(hi)} className="rounded p-1.5 text-muted-foreground hover:text-destructive" title="Quitar match" data-testid={`trip-kimkim-hotel-unlink-${hi}`}><X className="h-3.5 w-3.5" /></button>}
                            </div>
                          </div>
                          {isOpen && <InlineMatchPicker initialQuery={h.kimkim_name} dayCity={h.city} type="alojamiento" onPick={(r) => pickHotelMatch(hi, r)} onClose={() => setPickerOpen(null)} />}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between border-t pt-3">
                <div className="text-xs text-muted-foreground">Al crear el Trip te llevo directamente a su ficha para que revises las reservas.</div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose}>Cancelar</Button>
                  <Button onClick={confirm} disabled={confirming} data-testid="trip-kimkim-confirm">{confirming && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Crear Trip</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
